import React, { useEffect, useState } from 'react'

import styled from 'styled-components'

import defaultImage from '@/assets/images/default-image.png'

export interface ImageProps {
	src?: string
	alt?: string
	width?: string | number
	height?: string | number
	borderRadius?: string
	fallbackSrc?: string
	className?: string
	cssVars?: Record<string, string>
	customClass?: string
	onClick?: () => void
}

export const Image: React.FC<ImageProps> = ({
	src,
	alt = 'Image',
	width = '100%',
	height = 'auto',
	borderRadius = '8px',
	fallbackSrc = defaultImage,
	className = '',
	cssVars = {},
	customClass = '',
	onClick,
	...props
}) => {
	const [imageSrc, setImageSrc] = useState<string>(src || fallbackSrc)
	const [hasError, setHasError] = useState<boolean>(!src)

	useEffect(() => {
		if (src) {
			setImageSrc(src)
			setHasError(false)
		} else {
			setImageSrc(fallbackSrc)
			setHasError(true)
		}
	}, [src, fallbackSrc])

	const handleError = () => {
		if (!hasError) {
			setImageSrc(fallbackSrc)
			setHasError(true)
		}
	}

	// Create dynamic CSS variables
	const cssVariables = {
		'--image-border-radius': cssVars['borderRadius'] || borderRadius,
		'--image-width': cssVars['width'] || width,
		'--image-max-width': cssVars['maxWidth'] || '100%',
		'--image-height': cssVars['height'] || height,
		'--image-box-shadow': cssVars['boxShadow'] || '0',
	} as React.CSSProperties

	// Combine classes to allow custom CSS overrides
	const combinedClassName = `${className} ${customClass} mybt-image`.trim()

	return (
		<StyledImage
			src={imageSrc}
			alt={alt}
			style={cssVariables}
			className={combinedClassName}
			onClick={onClick}
			onError={handleError}
			$hasError={hasError}
			{...props}
		/>
	)
}

interface StyledImageProps {
	$hasError: boolean
}

const StyledImage = styled.img<StyledImageProps>`
	display: block;
	width: var(--image-width, 100%);
	max-width: var(--image-max-width, 100%);
	height: var(--image-height, auto);
	border-radius: var(--image-border-radius, 8px);
	box-shadow: var(--image-box-shadow, none);
	object-fit: cover;

	&.mybt-image {
		transition: transform 0.2s ease-in-out;

		${({ $hasError }) =>
			$hasError &&
			`
      background-color: #f0f0f0;
      opacity: 0.8;
    `}
	}
`

export default Image
